import { TrashIcon } from "@heroicons/react/24/solid";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useAlert } from "../../hooks";
import PrimaryButton from "../Buttons/PrimaryButton";
import { CheckboxInput, NumberInput, SelectInput, TextInput } from "../Inputs";
import PrimaryTable from "../Tables/PrimaryTable";
import ModalWrapper from "./ModalWrapper";
import constants from "../../utils/constants";

const NEW_PROPERTY = {
  name: "",
  type: constants.VARIABLE_TYPES[0].value,
  isNullable: false,
  maxLength: 30,
  foreignKey: undefined,
  detailView: true,
  tabularView: false,
  includeInSearchCriteria: false,
};

export default function StandardEntityModal({
  entity,
  entities,
  open,
  setOpen,
  handleAddEntity,
  domains,
  setDomains,
}) {
  const alert = useAlert();
  const [entityEdit, setEntityEdit] = useState({ ...entity });

  const [newProperty, setNewProperty] = useState({
    ...NEW_PROPERTY,
    id: uuidv4(),
  });
  const [newDomainName, setNewDomainName] = useState("");

  useEffect(() => {
    setEntityEdit({ ...entity });
  }, [entity]);

  const handleEntityChange = (property, value) => {
    setEntityEdit({
      ...entityEdit,
      [property]: value,
    });
  };

  const handlePropertyChange = (property, value) => {
    setNewProperty({
      ...newProperty,
      [property]: value,
    });
  };

  const handlePropertyChanges = (change) => {
    setNewProperty({
      ...newProperty,
      ...change,
    });
  };

  const handleAddProperty = () => {
    // TODO: add validations

    if (!newProperty.name || !newProperty.name.trim()) {
      alert("Invalid input", "Property must have a name", "warning");
      return false;
    }

    if (
      (newProperty.type === 0||
        newProperty.type === 1) &&
      (!newProperty.maxLength ||
        isNaN(newProperty.maxLength) ||
        newProperty.maxLength <= 0)
    ) {
      alert(
        "Invalid input",
        "String properties must have a positive numerical value for max length",
        "warning"
      );
      return false;
    }

    if (
      newProperty.foreignKey &&
      (!newProperty.foreignKey.name ||
        !newProperty.foreignKey.entity ||
        !newProperty.foreignKey.property)
    ) {
      alert(
        "Invalid input",
        "Make sure all required fields are filled out in the foreign key section",
        "warning"
      );
      return false;
    }

    let properties = [...entityEdit.properties, newProperty];

    setEntityEdit({ ...entityEdit, properties });
    setNewProperty({ ...NEW_PROPERTY, id: uuidv4() });
  };

  const handleRemoveProperty = (id) => {
    let properties = entityEdit.properties.filter((x) => x.id !== id);
    setEntityEdit({ ...entityEdit, properties });
  };

  const handleAddStandardEntity = () => {
    if (!entityEdit.name || !entityEdit.name.trim()) {
      alert("Invalid input", "Entity must have a name", "warning");
      return false;
    }

    if (
      entities
        .filter((x) => x.id !== entityEdit.id)
        .map((x) => x.name)
        .find((x) => x.toLowerCase() === entityEdit.name.toLowerCase())
    ) {
      alert("Invalid input", "Entity name already exists", "warning");
      return false;
    }

    if (entityEdit.includeInApiSpike && !entityEdit.domain) {
      alert(
        "Invalid input",
        "Entity has no domain but is included in api spike",
        "warning"
      );
      return false;
    }

    if (entityEdit.isSoftDeletable && !entityEdit.isUserManaged) {
      alert(
        "Invalid input",
        "Entity cannot be soft deletable and also user managed (if soft deleteable, it must be user managed)",
        "warning"
      );
      return false;
    }

    if (!entityEdit.properties || entityEdit.properties.length === 0) {
      alert("Invalid entity", "Entity has no properties", "warning");
      return false;
    }

    handleAddEntity(entityEdit);
    setOpen(false);
    clearValues();
  };

  const clearValues = () => {
    setNewProperty({ ...NEW_PROPERTY, id: uuidv4() });
    setNewDomainName("");
  };

  return (
    <ModalWrapper open={open} setOpen={setOpen}>
      <div className="w-[85vw] lg:w-[70vw]">
        <div className="pb-8 mx-6 text-center text-sm text-gray-500">
          Standard entity information
        </div>

        <div className="pb-8 mx-6 text-center text-sm text-gray-500">
          User, Role, UserRole, and UserAuthenticationAttempt are created by
          default -- do not include these
        </div>

        <div className="mb-8 grid gap-y-8">
          <div>
            <TextInput
              label="Table Name"
              placeholder="Enter Table Name (singular, PascalCase)"
              value={entityEdit.name || ""}
              onChange={(e) => handleEntityChange("name", e.target.value)}
            />
          </div>
          <CheckboxInput
            label="User managed"
            subLabel="Include fields createdAt, createdByUserId, updatedAt, updatedByUserId automatically (ie don't need to create these columns below for this entity)"
            checked={entityEdit.isUserManaged}
            onChange={() =>
              handleEntityChange("isUserManaged", !entityEdit.isUserManaged)
            }
          />
          <CheckboxInput
            label="Soft deletable"
            subLabel="Include fields deactivatedAt and deactivatedByUserId automatically (ie don't need to create these columns below for this entity)"
            checked={entityEdit.isSoftDeletable}
            onChange={() =>
              handleEntityChange("isSoftDeletable", !entityEdit.isSoftDeletable)
            }
          />
          <CheckboxInput
            label="Include in API spike"
            subLabel="If not included, only models, mappings, dtos, and automapper will be created for this entity. If included, react will come with this entity managed (and corresponding api spikes)"
            checked={entityEdit.includeInApiSpike}
            onChange={() =>
              handleEntityChange(
                "includeInApiSpike",
                !entityEdit.includeInApiSpike
              )
            }
          />
          {entityEdit.includeInApiSpike && (
            <div>
              <SelectInput
                label="Domain"
                subLabel="This will be the name of the controller, service, and repository that api spike will use"
                options={domains}
                value={domains.find((x) => x.value === entityEdit.domain)}
                onChange={(val) => handleEntityChange("domain", val.value)}
                placeholder="Domain"
                isSearchable
              />
              <div className="flex items-center space-x-4 text-xs italic text-gray-400 mt-2">
                Want a different domain?{" "}
                <div className="font-semibold ml-1">Add one now: </div>
                <TextInput
                  value={newDomainName}
                  onChange={(e) => setNewDomainName(e.target.value)}
                />
                <div>
                  <PrimaryButton
                    text="Add domain name"
                    disabled={
                      !newDomainName ||
                      _.find(
                        domains,
                        (x) =>
                          x.value.toLowerCase() === newDomainName.toLowerCase()
                      )
                    }
                    onClick={() => {
                      setDomains([
                        ...domains,
                        { label: newDomainName, value: newDomainName },
                      ]);
                      handleEntityChange("domain", {
                        label: newDomainName,
                        value: newDomainName,
                      });
                      setNewDomainName("");
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* New property */}
        <div>
          <div className="mb-2">New property &#40;column&#41;</div>
          <div className="grid gap-y-4 bg-gray-100 p-4">
            <div>
              <TextInput
                label="Property Name"
                placeholder="Enter Property Name (singular, PascalCase)"
                value={newProperty.name || ""}
                onChange={(e) => handlePropertyChange("name", e.target.value)}
                required
              />
            </div>
            <div>
              <SelectInput
                label="Property Type"
                options={constants.VARIABLE_TYPES}
                value={constants.VARIABLE_TYPES.find(x => x.value === newProperty.type)}
                onChange={(val) => handlePropertyChange("type", val.value)}
                placeholder="Variable type"
                isSearchable
                required
              />
            </div>
            {(newProperty.type === 0 ||
              newProperty.type === 1) && (
              <div>
                <NumberInput
                  label="Max. Length"
                  placeholder="Max character length"
                  value={newProperty.maxLength}
                  onChange={(e) =>
                    handlePropertyChange("maxLength", e.target.valueAsNumber)
                  }
                  required
                />
              </div>
            )}
            <CheckboxInput
              label="Nullable"
              subLabel="Property allows for null values"
              checked={newProperty.isNullable}
              onChange={() =>
                handlePropertyChange("isNullable", !newProperty.isNullable)
              }
            />
            <CheckboxInput
              label="Foreign Key"
              subLabel="Property is a foreign key"
              checked={newProperty.foreignKey !== undefined}
              onChange={() => {
                const fk = newProperty.foreignKey;
                handlePropertyChanges({
                  type: 9,
                  foreignKey: fk
                    ? undefined
                    : {
                        name: "",
                        property: "",
                        displayProperty: "",
                        entity: "",
                      },
                });
              }}
            />
            {entityEdit.includeInApiSpike && (
              <div className="grid gap-y-4">
                <CheckboxInput
                  label="Detail view"
                  subLabel="Show column within detail view in React (ie in edit page of entity)"
                  checked={newProperty.detailView}
                  onChange={() =>
                    handlePropertyChange("detailView", !newProperty.detailView)
                  }
                />
                <CheckboxInput
                  label="Tabular view"
                  subLabel="Show column within React table view"
                  checked={newProperty.tabularView}
                  onChange={() =>
                    handlePropertyChange(
                      "tabularView",
                      !newProperty.tabularView
                    )
                  }
                />
                <CheckboxInput
                  label="Include in search criteria/filtering"
                  subLabel="Show column within filters of React table view"
                  checked={newProperty.includeInSearchCriteria}
                  onChange={() =>
                    handlePropertyChange(
                      "includeInSearchCriteria",
                      !newProperty.includeInSearchCriteria
                    )
                  }
                />
              </div>
            )}
            {newProperty.foreignKey && (
              <div className="w-2/3 bg-gray-200 p-4 rounded-md">
                <div className="pb-2">Foreign key information</div>
                <TextInput
                  label="Entity name"
                  subLabel="Name of table fk references"
                  required
                  value={newProperty.foreignKey?.entity}
                  onChange={(e) =>
                    handlePropertyChange("foreignKey", {
                      ...newProperty.foreignKey,
                      entity: e.target.value,
                    })
                  }
                />
                <TextInput
                  label="Property name"
                  subLabel="Name of column fk references"
                  required
                  value={newProperty.foreignKey?.property}
                  onChange={(e) =>
                    handlePropertyChange("foreignKey", {
                      ...newProperty.foreignKey,
                      property: e.target.value,
                    })
                  }
                />
                <TextInput
                  label="FK name"
                  subLabel="(eg FK_FavoriteCustomerId_Restaurant)"
                  required
                  value={newProperty.foreignKey?.name}
                  onChange={(e) =>
                    handlePropertyChange("foreignKey", {
                      ...newProperty.foreignKey,
                      name: e.target.value,
                    })
                  }
                />
                <TextInput
                  label="FK display"
                  subLabel="Column in foreign entity to show (eg fk is to User Id column but display is User firstName"
                  value={newProperty.foreignKey?.displayProperty}
                  onChange={(e) =>
                    handlePropertyChange("foreignKey", {
                      ...newProperty.foreignKey,
                      displayProperty: e.target.value,
                    })
                  }
                />
              </div>
            )}
            <PrimaryButton
              text="Add property to entity"
              onClick={handleAddProperty}
            />
          </div>
        </div>

        {/* Existing properties */}
        <div className="mt-4">
          <div className="mb-2">Existing properties &#40;columns&#41;</div>
          <PrimaryTable
            paginatorHidden
            header={
              <PrimaryTable.Header>
                <PrimaryTable.HeaderColumn display="Name" />
                <PrimaryTable.HeaderColumn display="Type" />
                <PrimaryTable.HeaderColumn display="Nullable" />
                <PrimaryTable.HeaderColumn display="Max length" />
                <PrimaryTable.HeaderColumn display="Foreign key" />
                <PrimaryTable.HeaderColumn display="Detail view" />
                <PrimaryTable.HeaderColumn display="Tabular view" />
                <PrimaryTable.HeaderColumn display="Search criteria within tabular view" />
                <PrimaryTable.HeaderColumn />
              </PrimaryTable.Header>
            }
          >
            {_.map(entityEdit?.properties, (x) => (
              <PrimaryTable.Row key={`${x.value}`}>
                <PrimaryTable.Cell>{x.name}</PrimaryTable.Cell>
                <PrimaryTable.Cell>{x.type.label}</PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {x.isNullable ? "Yes" : "No"}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {x.type === 0|| x.type === 1
                    ? x.maxLength
                    : "N/A"}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {x.foreignKey ? "Yes" : "No"}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {entityEdit.includeInApiSpike
                    ? x.detailView
                      ? "Yes"
                      : "No"
                    : "N/A"}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {entityEdit.includeInApiSpike
                    ? x.tabularView
                      ? "Yes"
                      : "No"
                    : "N/A"}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {entityEdit.includeInApiSpike
                    ? x.includeInSearchCriteria
                      ? "Yes"
                      : "No"
                    : "N/A"}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  <TrashIcon
                    className="w-5 text-red-900 cursor-pointer hover:text-red-500"
                    onClick={() => handleRemoveProperty(x.id)}
                  />
                </PrimaryTable.Cell>
              </PrimaryTable.Row>
            ))}
          </PrimaryTable>
        </div>
        <div className="py-2" />
        <PrimaryButton
          text="Add Entity to Project"
          onClick={handleAddStandardEntity}
        />
      </div>
    </ModalWrapper>
  );
}
